import { productApiClient } from '@/clients/ProductAPIClient';
import {
  IAlbum,
  IAlbumMetaData,
  IMerchandiseMetadata,
} from '@/data/models/album';
import { IArtist } from '@/data/models/artist';
import { IGenre } from '@/data/models/genre';
import { IManufacturing } from '@/data/models/manufacturing';
import { IMarketing } from '@/data/models/marketing';
import { IPackaging } from '@/data/models/packaging';
import { ISalesProjection } from '@/data/models/salesProjection';
import {
  ErrorCode,
  IValidationResults,
  ValidationLevel,
} from '@/data/models/validation';
import { VideoContentType } from '@/data/models/videoContentType';
import useGetReleaseTypesQuery from '@/hooks/useGetReleaseTypesQuery';
import { ILoadAlbum, loadAlbum } from '@/state/actions/albumAction';
import store from '@/state/store';
import { logger } from '@/utils/Logger';
import { isVideoFormat, parseDateString } from '@/utils/helper';
import _, { isNull } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useGetAlbumValidationMetadataQuery from './useGetAlbumValidationMetadataQuery';
import useGetGenresQuery from './useGetGenresQuery';
import useGetMerchandiseTypesQuery from './useGetMerchandiseTypesQuery';
import useGetMetadataLanguagesQuery from './useGetMetadataLanguagesQuery';
import useGetPackagingQuery from './useGetPackagingQuery';
import useGetTerritoriesQuery from './useGetTerritoriesQuery';
import { IOptions } from '@/data/models/option';
import { useSelector } from 'react-redux';
import { getLoadAlbumState } from '@/selectors/album';
import { IChangeRequest } from '@/data/models/changeRequest';
import { changeRequestApiClient } from '@/clients/ChangeRequestAPIClient';
import { getSaveErrorMessage } from '@/utils/saveErrorMessages';
import { isValidationError } from '@/utils/validationErrors';
import { translateMerchandiseProductType } from '@/helpers/translateProductType';

interface UseProductEditModeProps {
  togglePopUp: () => void;
  albumObj: IAlbum;
  errorFlag: boolean;
  errors: IValidationResults[];
  showValidationErrors: boolean;
  onProductUpdated: () => void;
  changeRequest?: IChangeRequest;
}

/**
 * Custom hook managing the state, data fetching, validation, and business logic
 * for the Product Edit Mode (general product details form/modal).
 */
export const useProductEditMode = ({
  togglePopUp,
  albumObj,
  errorFlag,
  errors,
  showValidationErrors,
  onProductUpdated,
  changeRequest,
}: UseProductEditModeProps) => {
  const { t } = useTranslation();
  const initialAlbum = cloneDeep(albumObj);

  const initialManufacturing = initialAlbum.albumMetaData?.manufacturing;
  if (
    initialManufacturing &&
    initialManufacturing.manufacturingCostPerUnit != null &&
    String(initialManufacturing.manufacturingCostPerUnit) !== ''
  ) {
    const cost = Number(initialManufacturing.manufacturingCostPerUnit);
    if (!isNaN(cost)) {
      const [intPart, decPart] = cost.toFixed(2).split('.');
      (initialManufacturing as any).manufacturingCostPerUnit =
        `${intPart}.${decPart}`;
    }
  }

  // --- Entity & Form State ---
  const [album, setAlbum] = useState<IAlbum>(initialAlbum);
  const { data: packagings } = useGetPackagingQuery(album?.format?.id);
  const { data: merchandiseTypes } = useGetMerchandiseTypesQuery(
    album?.format?.id
  );
  const { data: metadataLanguages } = useGetMetadataLanguagesQuery();
  const { data: territories } = useGetTerritoriesQuery();
  const { data: genres } = useGetGenresQuery();
  const { data: albumValidationMetadata } = useGetAlbumValidationMetadataQuery(
    album.id ?? 0
  );

  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [formSubmitCount, setFormSubmitCount] = useState(0);
  const { isJustCreated } = useSelector(getLoadAlbumState);

  const yesOption = t('product.yes');
  const noOption = t('product.no');

  // --- UI / Modal State ---
  const [showPopUp, setShowPopUp] = useState(true);
  const [showCancelChangesModal, setShowCancelChangesModal] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorBanner, setErrorBanner] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const [albumErrors, setAlbumErrors] = useState<IValidationResults[]>([]);
  const [albumIssues, setAlbumIssues] = useState<IValidationResults[]>([]);
  const { data: releaseTypes } = useGetReleaseTypesQuery();

  // --- Specialized UI State for Dates & Accounts ---
  const [originalReleaseDateOpen, setOriginalReleaseDateOpen] = useState(false);
  const [useReleaseDateVisible, setUseReleaseDateVisible] = useState(false);
  const [activateUseReleaseDate, setActivateUseReleaseDate] = useState(false);
  const [onChangePreorderDate, setOnChangePreorderDate] = useState(false);
  const [onClickTargetAccount, setOnClickTargetAccount] = useState(false);
  const [onChangeTargetAccount, setOnChangeTargetAccount] = useState(false);

  useEffect(() => {
    // Process incoming validation errors and map them to the album fields
    errorMessagehandling();
  }, [errorFlag, errors, showValidationErrors]);

  const errorMessagehandling = () => {
    if (
      (errorFlag === true || showValidationErrors === true) &&
      errors.length !== 0 &&
      errors !== null
    ) {
      const albumIssues = errors.filter(
        (va) => va.validationLevel === ValidationLevel.Album
      );
      const albumErrorIssues = albumIssues.filter(isValidationError);
      setAlbumIssues(albumIssues);
      // Field styling uses both validation errors and warnings.
      setAlbumErrors(albumIssues);
      const productInfoMissingOrInvalidErrors: IValidationResults[] =
        albumErrorIssues.filter(
          (va) => va.errorCode === ErrorCode.ProductInfoMissingOrInvalid
        );
      const errorMessages: string[] = [];
      if (productInfoMissingOrInvalidErrors.length > 0) {
        errorMessages.push(
          t(
            'validationAlert.productEditMode.productInfoMissingandInvalidErrorMessage'
          )
        );
      }
      setErrorMessages(errorMessages);
      setErrorBanner(albumErrorIssues.length > 0);
    } else {
      setAlbumIssues([]);
      setAlbumErrors([]);
      setErrorMessages([]);
      setErrorBanner(false);
    }
  };

  const fieldHasErrors = (fieldName: string) => {
    return !!albumErrors.find((va) => va.fieldName === fieldName);
  };

  const showErrors = showValidationErrors && !isJustCreated;
  const fieldValidationState = (fieldName: string) => {
    if (!showErrors) return undefined;

    return albumErrors.find((va) => va.fieldName === fieldName)?.isValid;
  };

  const fieldHasVisibleErrors = (fieldName: string) => {
    return showErrors && fieldHasErrors(fieldName);
  };

  // --- Async Loaders for Searchable Dropdowns (React-Select) ---
  const loadLabels = (inputText: any, callback: any) => {
    productApiClient.getLabel({ value: inputText }).then((result) => {
      callback(result);
    });
  };

  const loadGenres = (inputText: string | null, callback: any) => {
    if (!inputText) {
      callback(genres);
    } else {
      const lowercaseInput = inputText?.toLowerCase();
      callback(
        genres?.filter((g) => g.label.toLowerCase().includes(lowercaseInput))
      );
    }
  };

  const loadReleaseTypes = (inputText: string | null, callback: any) => {
    const optionsWithEmpty = [
      { label: '', value: '' },
      ...(releaseTypes || []),
    ];

    if (!inputText) {
      callback(optionsWithEmpty);
    } else {
      const lowercaseInput = inputText?.toLowerCase();
      callback(
        optionsWithEmpty.filter((p) =>
          p.label.toLowerCase().includes(lowercaseInput)
        )
      );
    }
  };

  const loadMerchandiseTypes = (inputText: string | null, callback: any) => {
    if (!merchandiseTypes) return;
    const translatedTypes = merchandiseTypes.map((mType) => {
      return {
        ...mType,
        label: translateMerchandiseProductType(mType.label),
      };
    });
    if (!inputText) {
      callback(translatedTypes);
    } else {
      const lowercaseInput = inputText?.toLowerCase();
      callback(
        translatedTypes?.filter((p) =>
          p.label?.toLowerCase().includes(lowercaseInput)
        )
      );
    }
  };

  const loadPackaging = (inputText: string | null, callback: any) => {
    if (!inputText) {
      callback(packagings);
    } else {
      const lowercaseInput = inputText?.toLowerCase();
      callback(
        packagings?.filter((p) =>
          p.label.toLowerCase().includes(lowercaseInput)
        )
      );
    }
  };

  const loadTerritories = (inputText: string | null, callback: any) => {
    if (!inputText) {
      callback(territories);
    } else {
      const lowercaseInput = inputText?.toLowerCase();
      callback(
        territories?.filter((t) =>
          t.label.toLowerCase().includes(lowercaseInput)
        )
      );
    }
  };

  const loadMetadataLanguages = (inputText: string | null, callback: any) => {
    if (!inputText) {
      callback(metadataLanguages);
    } else {
      const lowercaseInput = inputText?.toLowerCase();
      callback(
        metadataLanguages?.filter((ml) =>
          ml.label.toLowerCase().includes(lowercaseInput)
        )
      );
    }
  };

  const isVideoProduct = useCallback(
    () => isVideoFormat(album.format?.name),
    [album.format?.name]
  );

  useEffect(() => {
    // Disable save if mandatory top-level fields are missing
    if (album.label.adminOrgId && album.title) {
      setIsSaveDisabled(false);
    } else {
      setIsSaveDisabled(true);
    }

    if (album.releaseDate) {
      // Determine if the "Use Release Date" button should be visible for Original Release Date
      if (album.releaseDate !== album.originalReleaseDate) {
        setUseReleaseDateVisible(true);
        setActivateUseReleaseDate(false);
      } else {
        setUseReleaseDateVisible(false);
      }
    }
  }, [album]);

  // --- Helper getters for nested objects ---
  const getAlbumMetaData = album.albumMetaData
    ? album.albumMetaData
    : ({} as IAlbumMetaData);

  const getAlbumManufacturing =
    album?.albumMetaData?.manufacturing ?? ({} as IManufacturing);

  const getAlbumSalesProjection =
    album?.albumMetaData?.salesProjection ?? ({} as ISalesProjection);

  const getMerchandiseMetadata =
    album.merchandiseMetadata ?? ({} as IMerchandiseMetadata);

  // --- Input Handlers ---
  const handleManufacturing = (field: string, value: any) => {
    const albumMetaData = getAlbumMetaData;
    const manufacturing = getAlbumManufacturing;

    switch (field) {
      // string fields
      case 'countryOfManufacture':
      case 'currency':
      case 'explicitLyrics':
        manufacturing[field] = value;
        break;

      // numeric fields
      case 'manufacturingCostPerUnit': {
        if (value === '' || value === null) {
          manufacturing[field] = null;
        } else if (/^\d{0,3}(\.\d{0,2})?$/.test(value)) {
          (manufacturing as any)[field] = value;
        }
        break;
      }

      default:
        logger.error(`cannot process manufacturing field ${field}`);
        break;
    }
    albumMetaData.manufacturing = manufacturing;
    setAlbum({ ...album, albumMetaData: albumMetaData });
  };

  const handleManufacturingCostBlur = () => {
    setAlbum((prevAlbum) => {
      const updatedAlbum = cloneDeep(prevAlbum);
      const manufacturing = updatedAlbum.albumMetaData?.manufacturing;

      if (
        manufacturing &&
        manufacturing.manufacturingCostPerUnit != null &&
        String(manufacturing.manufacturingCostPerUnit) !== ''
      ) {
        const cost = Number(manufacturing.manufacturingCostPerUnit);
        if (!isNaN(cost)) {
          const [intPart, decPart] = cost.toFixed(2).split('.');
          (manufacturing as any).manufacturingCostPerUnit =
            `${intPart}.${decPart}`;
        }
      }
      return updatedAlbum;
    });
  };

  const handleRadioButton = (field: string, e: any) => {
    const albumMetaData = getAlbumMetaData;
    const manufacturing = getAlbumManufacturing;
    const buttonBoolValue = e.target.value === yesOption ? true : false;

    switch (field) {
      // albumMetadata fields
      case 'exclusiveReleaseAccount':
      case 'retailPreOrder':
        albumMetaData[field] = buttonBoolValue;
        break;

      // manufacturing fields
      case 'isManufacturedByIngrooves':
      case 'ingroovesCanadaRight':
        manufacturing[field] = buttonBoolValue;
        albumMetaData.manufacturing = manufacturing;
        break;

      default:
        logger.error(`cannot process radioButton for field ${field}`);
        break;
    }
    setAlbum({ ...album, albumMetaData: albumMetaData });
  };

  const handleAlbumMetaData = (field: string, value: string | null) => {
    const albumMetaData = getAlbumMetaData;
    switch (field) {
      // numeric fields
      case 'boxCount':
      case 'unitsPerSets':
      case 'copyrightYear':
      case 'phonographicCopyrightYear': {
        if (value) {
          const intValue = parseInt(value);
          albumMetaData[field] = !isNaN(intValue) ? intValue : null;
        } else {
          albumMetaData[field] = null;
        }
        break;
      }

      case 'metadataLanguage':
      case 'copyrightLine':
      case 'phonographicCopyrightLine':
      case 'packagingDescription':
      case 'exclusiveReleaseAccountName':
      case 'internalDescription':
        albumMetaData[field] = value;
        break;

      case 'retailPreOrderDate':
        albumMetaData[field] = parseDateString(value);
        break;

      case 'retailPreOrderTime':
        albumMetaData[field] = value;
        break;

      default:
        logger.error(`cannot process albumMetaData field ${field}`);
        break;
    }

    setAlbum({ ...album, albumMetaData: albumMetaData });
  };

  const handleMerchandiseMetadata = (field: string, value: any) => {
    const merchandiseMetadata = getMerchandiseMetadata;
    switch (field) {
      case 'color':
      case 'size':
      case 'merchandiseType':
      case 'additionalInformation':
      case 'productDescription':
      case 'displayNameOverride':
        (merchandiseMetadata as any)[field] = value;
        break;
      default:
        logger.error(`cannot process merchandiseMetadata field ${field}`);
        break;
    }
    setAlbum({ ...album, merchandiseMetadata: merchandiseMetadata });
  };

  const handleSaleProjection = (field: string, value: any) => {
    const albumMetaData = getAlbumMetaData;
    const salesProjection = getAlbumSalesProjection;

    switch (field) {
      case 'weekOneProjection':
      case 'weekOneDigital':
      case 'weekOneDigitalSalesProjection':
      case 'weekOnePhysicialSalesProjection':
      case 'projectedPhysicalIO':
        // TODO these should be int? PRS-134
        salesProjection[field] = value;
        albumMetaData.salesProjection = salesProjection;
        break;
      default:
        logger.error(`cannot process SaleProjection for field ${field}`);
        break;
    }
    setAlbum({ ...album, albumMetaData: albumMetaData });
  };

  const handleAlbumField = (field: string, value: any) => {
    setAlbum({ ...album, [field]: value });
  };

  // --- Form Actions (Submit & Cancel) ---
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSaveErrorMessage(null);

    setFormSubmitCount(formSubmitCount + 1);

    const albumPayload = cloneDeep(album);
    if (albumPayload.albumSalesTeams) {
      albumPayload.albumSalesTeams = albumPayload.albumSalesTeams.map((t) => ({
        ...t,
        isCustomPrice: t.isCustomPrice ?? false,
      }));
    }

    const state = JSON.stringify(albumPayload);
    try {
      if (changeRequest) {
        await changeRequestApiClient.updateChangeRequest(
          changeRequest.albumId,
          changeRequest.id!,
          { ...changeRequest, changeRequestAlbum: albumPayload }
        );
      } else {
        await productApiClient.updateProduct(albumPayload.id!, state);
      }

      const loadAlbumValues: ILoadAlbum = {
        isJustCreated: false,
      };
      await store.dispatch(loadAlbum(loadAlbumValues));
      setShowPopUp(false);
      togglePopUp();
      onProductUpdated();
    } catch (error: any) {
      logger.error(error);
      setSaveErrorMessage(
        getSaveErrorMessage(
          error,
          !!changeRequest,
          'Unable to save product details. Please try again.'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Specific Field Handlers ---
  const handleGenre = (selectedOption: IGenre) => {
    const albumMetaData = getAlbumMetaData;
    albumMetaData.genre = selectedOption;
    setAlbum({ ...album, albumMetaData: albumMetaData });
  };

  const handleVersion = (versions: string[]) => {
    if (versions.length === 1 && versions[0] === '') {
      versions = [];
    }
    const albumMetaData = getAlbumMetaData;
    albumMetaData.versions = versions;
    setAlbum({ ...album, albumMetaData: albumMetaData });
  };

  const handleDisplayArtist = (artistData: IArtist[]): string => {
    let displayArtist: string = '';
    let displayMainArtist: string = '';
    let displayFeaturedArtist: string = '';
    const mainArtists = artistData.filter(
      ({ performerRole }) => performerRole === 'Main'
    );
    const featuredArtists = artistData.filter(
      ({ performerRole }) => performerRole === 'Featured'
    );
    // eslint-disable-next-line array-callback-return
    mainArtists.map((item) => {
      if (displayMainArtist === '') {
        displayMainArtist = item.name;
      } else {
        displayMainArtist = displayMainArtist + ', ' + item.name;
      }
    });

    // eslint-disable-next-line array-callback-return
    featuredArtists.map((item) => {
      if (displayFeaturedArtist === '') {
        displayFeaturedArtist = ' feat. ' + item.name;
      } else {
        displayFeaturedArtist = displayFeaturedArtist + ', ' + item.name;
      }
    });

    displayArtist = displayMainArtist + displayFeaturedArtist;

    return displayArtist;
  };

  const handleArtist = (artistData: IArtist[]) => {
    if (
      artistData.length === 1 &&
      artistData[0].name === '' &&
      artistData[0].performerRole === ''
    ) {
      artistData = [];
    }
    const artists = artistData.map((item: IArtist) => item);
    const displayArtist = handleDisplayArtist(artists);
    setAlbum({ ...album, artists: artists, displayArtist: displayArtist });
  };

  const handleAccountExclusiveChange = (e: any) => {
    handleRadioButton('exclusiveReleaseAccount', e);
    if (e.target.value === noOption) {
      handleAlbumMetaData('exclusiveReleaseAccountName', null);
    }
  };

  const handleLabelSelect = (selectedLabel: IOptions) => {
    setAlbum({
      ...album,
      label: {
        ...album.label,
        adminOrgId: selectedLabel?.value ?? '',
        name: selectedLabel?.label ?? '',
        parentOrgName: '',
      },
    });
  };

  const handleMarketingInfo = (field: string, value: any) => {
    const albumMetaData = getAlbumMetaData;
    const marketingInfo = albumMetaData.marketing
      ? albumMetaData.marketing
      : ({} as IMarketing);
    if (field === 'marketingInformation') {
      marketingInfo.marketingInformation = value;
    } else if (field === 'projectOverview') {
      marketingInfo.projectOverview = value;
    }
    albumMetaData.marketing = marketingInfo;
    setAlbum({ ...album, albumMetaData: albumMetaData });
  };

  const onCancel = () => {
    if (_.isEqual(album, initialAlbum)) {
      closePopUp();
    } else {
      setShowCancelChangesModal(true);
    }
  };

  const closePopUp = () => {
    setShowPopUp(false);
    togglePopUp();
  };

  const handleReleaseType = (selectedOption: any) => {
    const releaseTypeName = selectedOption?.label;
    const albumMetaData = getAlbumMetaData;

    if (releaseTypeName === '' || selectedOption === null) {
      albumMetaData.releaseType = null;
    } else {
      const releaseType = releaseTypes?.find(
        (item) => item.label === releaseTypeName
      );

      if (releaseType) {
        albumMetaData.releaseType = releaseType.label;
      }
    }
    setAlbum({ ...album, albumMetaData });
  };

  const handleVideoContentType = (
    selectedOption: IOptions<VideoContentType> | null
  ) => {
    const albumMetaData = getAlbumMetaData;

    albumMetaData.videoContentType = selectedOption?.value ?? null;
    setAlbum({ ...album, albumMetaData });
  };

  const handlePackaging = (selectedOption: any) => {
    const packageName = selectedOption?.label;
    if (packageName === '' || selectedOption === null) {
      setAlbum({ ...album, packaging: null });
    } else {
      const package_ = packagings?.find((item) => item.label === packageName);

      const prePackaging = album.packaging
        ? album.packaging
        : ({} as IPackaging);
      if (prePackaging && package_) {
        prePackaging.id = parseInt(package_.value);
        prePackaging.name = package_.label;
      }
      setAlbum({ ...album, packaging: prePackaging });
    }
  };

  // --- Date Handling Logic ---
  const useReleaseDate = (album: IAlbum) => {
    const releaseDate = album.releaseDate;

    setUseReleaseDateVisible(false);
    setActivateUseReleaseDate(true);
    setAlbum({ ...album, originalReleaseDate: releaseDate });
  };

  const onOriginalReleaseDateChange = (date: Date | [Date, Date] | null) => {
    if (Array.isArray(date)) {
      date = date[0];
    }
    setAlbum({ ...album, originalReleaseDate: parseDateString(date) });
  };

  const isOriginalReleaseDateInvalid = (album: IAlbum) => {
    if (!showErrors) return false;

    const originalReleaseDate = album?.originalReleaseDate ?? null;
    const releaseDate = album.releaseDate ?? null;
    let invalidDateRange = false;

    if (originalReleaseDate && releaseDate) {
      invalidDateRange = originalReleaseDate > releaseDate;
    }

    return (
      albumErrors.find((va) => va.fieldName === 'OriginalReleaseDate') &&
      !activateUseReleaseDate &&
      !isNull(originalReleaseDate) &&
      invalidDateRange
    );
  };

  const getCurrentReleaseDate = (album: IAlbum) => {
    const ReleaseDate = album.releaseDate;

    return ReleaseDate ? moment(ReleaseDate).toDate() : undefined;
  };

  const getPreviousReleaseDate = (album: IAlbum) => {
    const releaseDate = album.releaseDate;

    // Check if releaseDate exists, then subtract 1 day and convert to Date object
    return releaseDate
      ? moment(releaseDate).subtract(1, 'days').toDate()
      : undefined;
  };

  return {
    album,
    setAlbum,
    packagings,
    merchandiseTypes,
    metadataLanguages,
    territories,
    genres,
    albumValidationMetadata,
    errorMessages,
    errorBanner: showErrors && errorBanner,
    saveErrorMessage,
    setSaveErrorMessage,
    showPopUp,
    showCancelChangesModal,
    setShowCancelChangesModal,
    isSaveDisabled,
    isSubmitting,
    albumErrors: showErrors ? albumErrors : [],
    albumIssues: showErrors ? albumIssues : [],
    showErrors,
    originalReleaseDateOpen,
    setOriginalReleaseDateOpen,
    useReleaseDateVisible,
    activateUseReleaseDate,
    setActivateUseReleaseDate,
    onChangePreorderDate,
    setOnChangePreorderDate,
    onClickTargetAccount,
    setOnClickTargetAccount,
    onChangeTargetAccount,
    setOnChangeTargetAccount,
    releaseTypes,
    yesOption,
    noOption,
    loadLabels,
    loadGenres,
    loadReleaseTypes,
    loadPackaging,
    loadMerchandiseTypes,
    loadTerritories,
    loadMetadataLanguages,
    isVideoProduct,
    handleManufacturing,
    handleManufacturingCostBlur,
    handleRadioButton,
    handleAlbumMetaData,
    handleMerchandiseMetadata,
    handleSaleProjection,
    handleAlbumField,
    handleSubmit,
    handleGenre,
    handleVersion,
    handleArtist,
    handleAccountExclusiveChange,
    handleLabelSelect,
    handleMarketingInfo,
    onCancel,
    closePopUp,
    handleReleaseType,
    handleVideoContentType,
    handlePackaging,
    useReleaseDate,
    onOriginalReleaseDateChange,
    isOriginalReleaseDateInvalid,
    getCurrentReleaseDate,
    fieldHasErrors: fieldHasVisibleErrors,
    fieldValidationState,
    getPreviousReleaseDate,
  };
};
